import { BoxV2 as Box, Button, Flex, TextInput } from 'portal-commons';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { FieldErrors, useForm } from 'react-hook-form';
import { faTags, faSplit } from '@fortawesome/pro-regular-svg-icons';
import { Grid } from '@material-ui/core';

import BillingCategoryRow from './BillingCategoryRow';
import BrandSuggestions from './BrandSuggestions';
import BrandVetBanner from './BrandVetBanner';
import BrandVetModal from './BrandVetModal';
import ChannelChangeModal from './ChannelChangeModal';
import ChannelSelector from './ChannelSelector';
import FormSection from '../FormSection';
import HostingRegionRow from './HostingRegionRow';
import ImportantInformationBanner from './ImportantInformationBanner';
import RcsVetPendingModal from './RcsVetPendingModal';
import TableRowSelector from './TableRowSelector';
import UseCaseRow from './UseCaseRow';
import {
  BillingCategoryHeadRows,
  ChannelOptions,
  HostingRegionHeadRows,
  RcsDisabledChannelOptions,
  UseCaseHeadRows,
} from './constants';
import { useControllers } from './hooks';
import { BrandSelectionFormValues } from './types';
import UsecasesList from '../UsecasesList';
import UseCaseAlertModal from '../UseCaseSelectionForm/UseCaseAlertModal';
import { fetchBrandSuggestions, getCnpList } from '../../apis';
import {
  BillingCategory,
  Brand,
  CampaignBuilderMetadata,
  HostingRegion,
  UseCase,
  VettingClass,
  VettingProvider,
  VettingRecord,
  brandController,
  campaignBuilderController,
  enumController,
  evpController,
} from '@/apis/core';
import { toastFlashMessage } from '@/utils';
import { Cnp, SelectItem } from '@/utils/types';
import { EntityTypes } from '@/shared_elements/enums';
import { useAuthContext } from '@/contexts';

interface BrandSelectionFormProps {
  tenDlcUseCases?: UseCase[];
  hostingRegions?: HostingRegion[];
  billingCategories?: BillingCategory[];
  rcsUseCases?: UseCase[];
  defaultValues?: BrandSelectionFormValues;
  onChange?: (values: BrandSelectionFormValues) => void;
  onCancel?: () => void;
  onNext?: (data?: { metadata?: CampaignBuilderMetadata; cnps: Cnp[] }) => void;
  readonlyBrand?: boolean;
  disableChannel?: '10DLC' | 'RCS' | 'BOTH';
}

enum ModalType {
  None = 'NONE',
  ChannelChange = 'CHANNEL_CHANGE',
  BrandVet = 'BRAND_VET',
  RcsVetPending = 'RCS_VET_PENDING',
  WeaAlert = 'WEA_ALERT',
}

const Container: FunctionComponent = ({ children }) => (
  <Flex sx={{ flexDirection: 'column', gap: '30px', width: '100%' }}>
    {children}
  </Flex>
);

const ActionContainer: FunctionComponent = ({ children }) => (
  <Flex
    sx={{
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '9px',
      width: '100%',
      marginTop: '10px',
    }}
  >
    {children}
  </Flex>
);

const BrandSelectionForm: FunctionComponent<BrandSelectionFormProps> = ({
  tenDlcUseCases,
  hostingRegions,
  billingCategories,
  rcsUseCases,
  defaultValues,
  readonlyBrand,
  disableChannel,
  onChange,
  onCancel,
  onNext,
}) => {
  const {
    formState: { errors },
    control,
    handleSubmit,
    watch,
  } = useForm<BrandSelectionFormValues>({
    defaultValues,
  });
  const {
    brandUid,
    setBrandUid,
    brandName,
    setBrandName,
    brandNameError,
    channelId,
    setChannelId,
    channelIdError,
    tenDlcUseCase,
    setTenDlcUseCase,
    tenDlcSubUseCases,
    setTenDlcSubUseCases,
    hostingRegion,
    setHostingRegion,
    hostingRegionError,
    billingCategory,
    setBillingCategory,
    billingCategoryError,
    rcsUseCase,
    setRcsUseCase,
    rcsUseCaseError,
  } = useControllers(control);
  const [vettingClasses, setVettingClasses] = useState<VettingClass[]>([]);
  const [vettingProviders, setVettingProviders] = useState<VettingProvider[]>(
    []
  );
  const [brandOptionsLoading, setBrandOptionsLoading] = useState(false);
  const [brandOptions, setBrandOptions] = useState<SelectItem[]>([]);
  const [brandSuggestionsShown, setBrandSuggestionsShown] = useState(false);
  const [brand, setBrand] = useState<Brand>();
  const [rcsVettingRecords, setRcsVettingRecords] = useState<VettingRecord[]>(
    []
  );
  const [tenDlcCampaignBuilderMetadata, setTenDlcCampaignBuilderMetadata] =
    useState<CampaignBuilderMetadata[]>([]);
  const [rcsCampaignBuilderMetadata, setRcsCampaignBuilderMetadata] = useState<
    CampaignBuilderMetadata[]
  >([]);
  const [importantInformationOpen, setImportantInformationOpen] =
    useState(false);
  const [modalType, setModalType] = useState(ModalType.None);
  const [newChannelId, setNewChannelId] = useState<string>();
  const [proceeding, setProceeding] = useState(false);
  const previousChannelId = useRef(defaultValues?.channelId);
  const previousTenDlcUseCase = useRef(defaultValues?.tenDlcUseCase);
  const { profile } = useAuthContext();
  const isRcsBlocked = !profile?.rcsEnabled && channelId === 'RCS';

  const rcsBrandError = useMemo(
    () =>
      brandUid && channelId === 'RCS' && rcsVettingRecords.length < 1
        ? 'RCS Vet required for selected brand to complete RCS campaign registration'
        : '',
    [channelId, rcsVettingRecords]
  );
  const channelOptions = useMemo(() => {
    if (
      brand?.entityType === EntityTypes.SoleProprietor ||
      disableChannel === 'RCS'
    ) {
      return RcsDisabledChannelOptions;
    }
    if (disableChannel === '10DLC') {
      return ChannelOptions.map((o) =>
        o.value === '10DLC' ? { ...o, disabled: true } : o
      );
    }
    if (disableChannel === 'BOTH') {
      return ChannelOptions.map((o) => ({ ...o, disabled: true }));
    }
    return ChannelOptions;
  }, [brand?.entityType, disableChannel]);

  const reloadBrand = async () => {
    const [brandResult, vettingRecordsResult] = await Promise.allSettled([
      brandController.getBrand(brandUid),
      brandController.getVettingRecords(brandUid, {
        vettingClass: 'RCS',
        vettingStatus: 'PENDING,ACTIVE',
      }),
    ]);
    if (brandResult.status === 'fulfilled') {
      const brand = brandResult.value;
      setBrand(brand);
    }
    if (vettingRecordsResult.status === 'fulfilled') {
      setRcsVettingRecords(vettingRecordsResult.value);
    }
  };

  const proceed = async () => {
    setProceeding(true);
    if (channelId === '10DLC') {
      const useCaseId = tenDlcUseCase!.id;
      const metadata = tenDlcCampaignBuilderMetadata.find(
        (record) => record.usecase === useCaseId
      );
      const cnps =
        useCaseId === 'SOLE_PROPRIETOR'
          ? await getCnpList({
              soleProprietorEnabled: true,
            })
          : await getCnpList();
      onNext?.({ metadata, cnps });
    } else if (channelId === 'RCS') {
      const metadata = rcsCampaignBuilderMetadata.find(
        (record) => record.usecase === rcsUseCase
      );
      const cnps = await getCnpList({ channelId });
      onNext?.({ metadata, cnps });
    }
    setProceeding(false);
  };

  const handleValid = async () => {
    if (
      channelId === '10DLC' &&
      (tenDlcUseCase?.id === 'CARRIER_EXEMPT' ||
        tenDlcUseCase?.id === 'EMERGENCY')
    ) {
      setModalType(ModalType.WeaAlert);
    } else if (
      channelId === 'RCS' &&
      rcsVettingRecords.some((record) => record.vettingStatus === 'PENDING')
    ) {
      setModalType(ModalType.RcsVetPending);
    } else {
      proceed();
    }
  };

  const handleInvalid = (errors: FieldErrors<BrandSelectionFormValues>) => {
    if (errors.tenDlcUseCase?.message) {
      toastFlashMessage(errors.tenDlcUseCase.message, 'error');
    }
    if (errors.tenDlcSubUseCases?.message) {
      toastFlashMessage(errors.tenDlcSubUseCases.message, 'error');
    }
  };

  const fetchBrandOptions = async (queries: Record<string, any> = {}) => {
    setBrandOptionsLoading(true);
    const suggestions = await fetchBrandSuggestions({
      ...queries,
      status: 'ACTIVE',
    });
    const options: SelectItem[] = [];
    suggestions.forEach((suggestion) => {
      const brandUids = suggestion.brandUid.split(',');
      brandUids.forEach((brandUid) => {
        options.push({
          label: suggestion.brandName,
          value: brandUid,
        });
      });
    });
    setBrandOptions(options);
    setBrandOptionsLoading(false);
  };

  const handleBrandNameChange = (value: string) => {
    setBrandName(value);
    setBrandSuggestionsShown(!!value);
    if (value.trim().length >= 2) {
      fetchBrandOptions({
        prefix: value.trim(),
      });
    } else {
      setBrandOptions([]);
    }
  };

  const handleBrandSelect = (option?: SelectItem) => {
    setBrandUid(option?.value ?? '');
    setBrandName(option?.label ?? '');
    setBrandSuggestionsShown(false);
  };

  const handleBrandReset = () => {
    setBrandUid('');
    setBrandName('');
    setBrandSuggestionsShown(false);
  };

  const handleChannelIdChange = (channelId: string) => {
    if (previousChannelId.current && channelId !== previousChannelId.current) {
      setModalType(ModalType.ChannelChange);
      setNewChannelId(channelId);
    } else {
      setChannelId(channelId);
    }
  };

  const handleTenDlcUseCaseOrSubUseCasesChange = (
    value: UseCase | string,
    type: string
  ) => {
    if (type === 'usecase') {
      setTenDlcUseCase(value);
    } else if (type === 'subUsecases') {
      const newSubUseCases = [...tenDlcSubUseCases];
      const index = newSubUseCases.indexOf(value as string);
      if (index === -1) {
        newSubUseCases.push(value as string);
      } else {
        newSubUseCases.splice(index, 1);
      }
      setTenDlcSubUseCases(newSubUseCases);
    }
  };

  const handleImportantInformationDismiss = () => {
    setImportantInformationOpen(false);
  };

  useEffect(() => {
    (async () => {
      const [vettingClassesResult, vettingProvidersResult] =
        await Promise.allSettled([
          enumController.getVettingClasses(),
          evpController.getVettingProviders(),
        ]);
      if (vettingClassesResult.status === 'fulfilled') {
        setVettingClasses(
          vettingClassesResult.value.filter(
            (vettingClass) => vettingClass.id === 'RCS'
          )
        );
      }
      if (vettingProvidersResult.status === 'fulfilled') {
        setVettingProviders(
          vettingProvidersResult.value.filter((vettingProvider) =>
            vettingProvider.vettingClasses.includes('RCS')
          )
        );
      }
    })();

    const { unsubscribe } = watch((values) => {
      onChange?.(values as BrandSelectionFormValues);
    });
    return () => unsubscribe();
  }, [watch]);

  useEffect(() => {
    (async () => {
      if (brandUid) {
        const [
          brandResult,
          vettingRecordsResult,
          campaignBuilderMetadataResult,
        ] = await Promise.allSettled([
          brandController.getBrand(brandUid),
          brandController.getVettingRecords(brandUid, {
            vettingClass: 'RCS',
            vettingStatus: 'PENDING,ACTIVE',
          }),
          campaignBuilderController.getCampaignBuilderMetadata(brandUid),
        ]);
        if (brandResult.status === 'fulfilled') {
          const brand = brandResult.value;
          setBrand(brand);
          if (brand) {
            if (brand?.entityType === EntityTypes.SoleProprietor) {
              setChannelId('10DLC');
              setRcsCampaignBuilderMetadata([]);
            } else {
              const campaignBuilderMetadata =
                await campaignBuilderController.getCampaignBuilderMetadata(
                  brandUid,
                  {
                    channelId: 'RCS',
                  },
                  {
                    silent: true,
                  }
                );
              setRcsCampaignBuilderMetadata(campaignBuilderMetadata);
            }
          }
        } else {
          setBrand(undefined);
        }
        if (vettingRecordsResult.status === 'fulfilled') {
          setRcsVettingRecords(vettingRecordsResult.value);
        } else {
          setRcsVettingRecords([]);
        }
        if (campaignBuilderMetadataResult.status === 'fulfilled') {
          setTenDlcCampaignBuilderMetadata(campaignBuilderMetadataResult.value);
        } else {
          setTenDlcCampaignBuilderMetadata([]);
        }
      } else {
        setBrand(undefined);
        setRcsVettingRecords([]);
        setTenDlcCampaignBuilderMetadata([]);
        setRcsCampaignBuilderMetadata([]);
      }
    })();
  }, [brandUid]);

  useEffect(() => {
    if (previousChannelId.current && channelId !== previousChannelId.current) {
      setTenDlcUseCase(undefined);
      setTenDlcSubUseCases([]);
      setHostingRegion('');
      setBillingCategory('');
      setRcsUseCase('');
    }
    if (channelId === 'RCS' && channelId !== previousChannelId.current) {
      setImportantInformationOpen(true);
    }
    previousChannelId.current = channelId;
  }, [channelId]);

  useEffect(() => {
    if (tenDlcUseCase?.id !== previousTenDlcUseCase.current?.id) {
      setTenDlcSubUseCases([]);
    }
    previousTenDlcUseCase.current = tenDlcUseCase;
  }, [tenDlcUseCase]);

  useEffect(() => {
    if (isRcsBlocked) {
      toastFlashMessage(
        'CSP account is not enabled for RCS features.',
        'error'
      );
    }
  }, [isRcsBlocked]);

  return (
    <form noValidate onSubmit={handleSubmit(handleValid, handleInvalid)}>
      <Container>
        <FormSection
          icon={faTags}
          title="Brand"
          message="Select a verified brand for registration."
        >
          <Box sx={{ position: 'relative', width: '400px' }}>
            <TextInput
              error={!!brandNameError?.message || !!rcsBrandError}
              label="Enter Brand DBA Name"
              value={brandName}
              note={brandNameError?.message || rcsBrandError}
              onChange={handleBrandNameChange}
              readOnly={readonlyBrand}
              data-testid="brandName"
            />
            {brandSuggestionsShown && (
              <BrandSuggestions
                loading={brandOptionsLoading}
                text={brandName}
                options={brandOptions}
                onSelect={handleBrandSelect}
                onClickAway={handleBrandReset}
              />
            )}
          </Box>
        </FormSection>
        <FormSection
          icon={faSplit}
          title="Channel"
          message="Select 10DLC or RCS for the type of campaign to register."
          error={channelIdError?.message}
        >
          <ChannelSelector
            options={channelOptions}
            value={channelId}
            onChange={handleChannelIdChange}
          />
        </FormSection>
        {channelId === '10DLC' && (
          <FormSection>
            <Grid container spacing={4}>
              <Grid item xs={6}>
                <UsecasesList
                  type="STANDARD"
                  title="Standard Campaign Type"
                  usecases={tenDlcUseCases}
                  usecase={tenDlcUseCase ?? {}}
                  campaignInfo={{
                    subUsecases: tenDlcSubUseCases,
                  }}
                  handleChange={handleTenDlcUseCaseOrSubUseCasesChange}
                  brandUsecases={tenDlcCampaignBuilderMetadata}
                />
              </Grid>
              <Grid item xs={6}>
                <UsecasesList
                  type="SPECIAL"
                  title="Special Campaign Type"
                  usecases={tenDlcUseCases}
                  usecase={tenDlcUseCase ?? {}}
                  campaignInfo={{
                    subUsecases: tenDlcSubUseCases,
                  }}
                  handleChange={handleTenDlcUseCaseOrSubUseCasesChange}
                  brandUsecases={tenDlcCampaignBuilderMetadata}
                />
              </Grid>
            </Grid>
          </FormSection>
        )}
        {rcsBrandError && (
          <BrandVetBanner onApply={() => setModalType(ModalType.BrandVet)} />
        )}
        {channelId === 'RCS' && !rcsBrandError && (
          <>
            {importantInformationOpen && (
              <ImportantInformationBanner
                onDismiss={handleImportantInformationDismiss}
              />
            )}
            <Flex sx={{ flexDirection: 'row', gap: '23px' }}>
              <FormSection>
                <TableRowSelector
                  headRows={HostingRegionHeadRows}
                  error={!!hostingRegionError?.message}
                  note={hostingRegionError?.message}
                  options={hostingRegions}
                  renderRow={(option) => (
                    <HostingRegionRow
                      key={option.id}
                      checked={option.id === hostingRegion}
                      option={option}
                      onChange={setHostingRegion}
                      data-testid={`hostingRegionSelector-${option.id}`}
                    />
                  )}
                  data-testid="hostingRegionSelector"
                />
              </FormSection>
              <FormSection>
                <TableRowSelector
                  headRows={BillingCategoryHeadRows}
                  error={!!billingCategoryError?.message}
                  note={billingCategoryError?.message}
                  options={billingCategories}
                  renderRow={(option) => (
                    <BillingCategoryRow
                      key={option.id}
                      checked={option.id === billingCategory}
                      option={option}
                      onChange={setBillingCategory}
                      data-testid={`billingCategorySelector-${option.id}`}
                    />
                  )}
                  data-testid="billingCategorySelector"
                />
              </FormSection>
              <FormSection>
                <TableRowSelector
                  headRows={UseCaseHeadRows}
                  error={!!rcsUseCaseError?.message}
                  note={rcsUseCaseError?.message}
                  options={rcsUseCases}
                  renderRow={(option) => (
                    <UseCaseRow
                      key={option.id}
                      checked={option.id === rcsUseCase}
                      option={option}
                      onChange={() => setRcsUseCase(option.id)}
                      data-testid={`rcsUseCaseSelector-${option.id}`}
                    />
                  )}
                  data-testid="rcsUseCaseSelector"
                />
              </FormSection>
            </Flex>
          </>
        )}
        <ActionContainer>
          <Button variant="outline" disabled={proceeding} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              isRcsBlocked ||
              proceeding ||
              Object.values(errors).some((error) => !!error?.message)
            }
          >
            {proceeding ? 'Processing..' : 'Next'}
          </Button>
        </ActionContainer>
      </Container>
      <ChannelChangeModal
        open={modalType === ModalType.ChannelChange}
        onCancel={() => setModalType(ModalType.None)}
        onSubmit={() => {
          setModalType(ModalType.None);
          setChannelId(newChannelId);
          setNewChannelId(undefined);
        }}
      />
      <RcsVetPendingModal
        open={modalType === ModalType.RcsVetPending}
        onCancel={() => setModalType(ModalType.None)}
        onSubmit={() => {
          proceed();
          setModalType(ModalType.None);
        }}
      />
      <BrandVetModal
        open={modalType === ModalType.BrandVet}
        brand={brand}
        vettingClasses={vettingClasses}
        vettingProviders={vettingProviders}
        onCancel={() => setModalType(ModalType.None)}
        onSubmit={() => {
          reloadBrand();
          setModalType(ModalType.None);
        }}
      />
      <UseCaseAlertModal
        open={modalType === ModalType.WeaAlert}
        useCase={tenDlcUseCase}
        onClose={() => setModalType(ModalType.None)}
        onSubmit={() => {
          proceed();
          setModalType(ModalType.None);
        }}
      />
    </form>
  );
};

export default BrandSelectionForm;
